'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { t } from '@/data/translations';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  statementText: string;
  countryName: string;
  statementId: string;
  langCode: string;
}

// Robust clipboard write that falls back to a hidden textarea if the modern
// API isn't available (older browsers, http contexts, some embedded webviews).
async function writeToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export default function ShareModal({ isOpen, onClose, statementText, countryName, statementId, langCode }: ShareModalProps) {
  // Use the live origin at runtime — env var was baked at build with localhost.
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://earth2america.com';
  const shareUrl = `${siteUrl}/s/${statementId}`;
  const shareText = `"${statementText.slice(0, 200)}${statementText.length > 200 ? '...' : ''}" - ${countryName} to America`;
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(shareUrl);

  // 'copied' = link was copied; 'linkedin' = text was copied for LinkedIn paste
  const [toast, setToast] = useState<null | 'copied' | 'linkedin'>(null);

  const flash = (kind: 'copied' | 'linkedin') => {
    setToast(kind);
    setTimeout(() => setToast(null), 2000);
  };

  const handleCopyLink = async () => {
    const ok = await writeToClipboard(shareUrl);
    if (ok) flash('copied');
  };

  const handleLinkedIn = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    // LinkedIn's share endpoint strips text/title — copy the post text first
    // so the user can paste it into their LinkedIn composer.
    e.preventDefault();
    await writeToClipboard(shareText + '\n\n' + shareUrl);
    flash('linkedin');
    // Open after a tiny delay so the toast is visible.
    setTimeout(() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, '_blank', 'noopener,noreferrer'), 600);
  };

  const platforms: { name: string; icon: string; color: string; url?: string; onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void }[] = [
    {
      name: 'X / Twitter',
      icon: '𝕏',
      url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      color: 'bg-black border-slate-600',
    },
    {
      name: 'Facebook',
      icon: 'f',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      color: 'bg-blue-600 border-blue-500',
    },
    {
      name: 'WhatsApp',
      icon: '💬',
      url: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`,
      color: 'bg-green-600 border-green-500',
    },
    {
      name: 'Telegram',
      icon: '✈',
      url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      color: 'bg-sky-500 border-sky-400',
    },
    {
      name: 'Reddit',
      icon: 'r/',
      url: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedText}`,
      color: 'bg-orange-600 border-orange-500',
    },
    {
      name: 'LinkedIn',
      icon: 'in',
      onClick: handleLinkedIn,
      color: 'bg-blue-700 border-blue-600',
    },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/70 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-x-0 bottom-0 z-50 bg-slate-900 rounded-t-3xl border-t border-slate-700/50 p-5 pb-8"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            <div className="w-10 h-1 bg-slate-700 rounded-full mx-auto mb-4" />

            <div className="flex items-start justify-between gap-3 mb-1">
              <h3 className="text-white font-bold text-lg" lang={langCode}>{t('shareTitle', langCode)}</h3>
              <button
                onClick={onClose}
                aria-label="Close"
                className="text-slate-500 hover:text-white p-1 -mt-1 -mr-1 transition-colors shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-slate-500 text-sm mb-4 line-clamp-2">{statementText}</p>

            <div className="grid grid-cols-3 gap-3 mb-4">
              {platforms.map(p => (
                <a
                  key={p.name}
                  href={p.url ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={p.onClick}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-2xl border ${p.color} hover:opacity-80 transition-opacity`}
                >
                  <span className="text-white text-lg font-bold">{p.icon}</span>
                  <span className="text-white text-xs">{p.name}</span>
                </a>
              ))}
            </div>

            <button
              onClick={handleCopyLink}
              className={`w-full py-3 rounded-2xl border font-medium transition-colors flex items-center justify-center gap-2 ${
                toast === 'copied'
                  ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                  : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-750'
              }`}
              lang={langCode}
            >
              {toast === 'copied' ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t('copied', langCode)}
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  {t('copyLink', langCode)}
                </>
              )}
            </button>

            {toast === 'linkedin' && (
              <p className="text-emerald-400 text-xs mt-3 text-center" lang={langCode}>
                {t('linkedinHint', langCode)}
              </p>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
